// Canonical finance categories (Phase 1).
//
// The `category` column on BOTH finance_transactions and finance_budgets stores
// a STABLE SLUG (food, transport, …) — never a translated label. The UI looks up
// the display label via `finance.cat.<slug>` i18n keys, and the budget×real match
// compares by slug, so it works in any language.
//
// User-defined custom categories are intentionally out of scope for Phase 1.

export type CategoryKind = "expense" | "income";

export interface CategoryDef {
  slug: string;
  emoji: string;
  kind: CategoryKind;
}

export const EXPENSE_CATEGORIES: CategoryDef[] = [
  { slug: "food", emoji: "🍽️", kind: "expense" },
  { slug: "transport", emoji: "🚗", kind: "expense" },
  { slug: "housing", emoji: "🏠", kind: "expense" },
  { slug: "health", emoji: "💊", kind: "expense" },
  { slug: "leisure", emoji: "🎬", kind: "expense" },
  { slug: "education", emoji: "📚", kind: "expense" },
  { slug: "shopping", emoji: "🛍️", kind: "expense" },
  { slug: "bills", emoji: "🧾", kind: "expense" },
  { slug: "other", emoji: "📦", kind: "expense" },
];

export const INCOME_CATEGORIES: CategoryDef[] = [
  { slug: "salary", emoji: "💼", kind: "income" },
  { slug: "freelance", emoji: "🔧", kind: "income" },
  { slug: "investment", emoji: "📈", kind: "income" },
  { slug: "gift", emoji: "🎁", kind: "income" },
  { slug: "other_income", emoji: "💰", kind: "income" },
];

export const ALL_CATEGORIES: CategoryDef[] = [...EXPENSE_CATEGORIES, ...INCOME_CATEGORIES];

export const CATEGORY_BY_SLUG: Record<string, CategoryDef> = Object.fromEntries(
  ALL_CATEGORIES.map((c) => [c.slug, c]),
);

export function categoriesFor(kind: CategoryKind): CategoryDef[] {
  return kind === "income" ? INCOME_CATEGORIES : EXPENSE_CATEGORIES;
}

export function categoryEmoji(slug: string | null | undefined): string {
  return (slug && CATEGORY_BY_SLUG[slug]?.emoji) || "💸";
}

// i18n key for a slug's display label. Falls back to the `other` label for an
// unknown/legacy slug so the UI never renders a raw `finance.cat.x` key.
export function categoryLabelKey(slug: string | null | undefined): string {
  if (slug && CATEGORY_BY_SLUG[slug]) return "finance.cat." + slug;
  return "finance.cat.other";
}
